import dayjs from 'dayjs'
import gql from 'graphql-tag'
import { useEffect, useRef, useState } from 'react'
import { useApolloClient } from 'react-apollo'
import EventStartEndDateTime from '../api/events/EventStartEndDateTime'
import { formRawWallClock, parseLocalWallClock, selectedRepeatDays } from '../api/events/helpers'
import type { OccurrencesPreviewInput } from '../api/events/helpers'
import { EventTypes, RepeatingPatterns } from '../util/constants'

export type { OccurrencesPreviewInput } from '../api/events/helpers'

// Phase 11 (design rev.5 Open-risks option (a); plan Phase 11) — the
// SERVER-FED occurrences live preview.
//
// As of Phase 8a the SERVER expands a repeating series in the resolved venue
// zone (DST-correct) and prepareToSubmit strips `occurrences` from the submit
// payload. The pre-Phase-11 EventNew preview computed occurrences in the
// BROWSER zone, so for a cross-zone / DST-boundary submitter it could show a
// series DIFFERENT from what is actually stored. This hook replaces that
// client computation: it queries `previewEventOccurrences`, which runs the
// SAME server pipeline EventCreate uses (Events::CreateLeadFromWallClock +
// Events::ExpandOccurrences — single source of truth, proven by a parity
// rspec), so the preview is provably IDENTICAL to the stored series.
//
// TRUTHFULNESS: the query returns each occurrence's NAIVE VENUE WALL-CLOCK
// ("YYYY-MM-DDTHH:MM" in the resolved zone — the Phase-6 *_local contract,
// i.e. what the public feed displays). We parse those with parseLocalWallClock
// (LOCAL construction, NOT `new Date(<absolute instant>)` which re-zones into
// the browser — the #877 bug), so the preview shows the exact venue
// wall-clock that will be stored, for same-zone AND cross-zone submitters
// alike. The shared formRawWallClock feeds the query the IDENTICAL raw
// wall-clock prepareToSubmit submits — no client-side drift.
//
// Patterned on useDuplicateCheck / useCrossZoneConfirm: a debounced
// query, fail-open (a failed lookup just yields no preview — never blocks the
// form), keyed on the recurrence-relevant form state.

const PREVIEW_EVENT_OCCURRENCES_QUERY = gql`
  query PreviewEventOccurrences(
    $startAtLocal: String!
    $endAtLocal: String
    $allDay: Boolean
    $eventType: String!
    $repeats: String
    $repeatDays: [String!]
    $repeatStopsOn: Date
    $timeZone: String
    $placeId: ID
    $primaryCommunityId: ID
  ) {
    previewEventOccurrences(
      startAtLocal: $startAtLocal
      endAtLocal: $endAtLocal
      allDay: $allDay
      eventType: $eventType
      repeats: $repeats
      repeatDays: $repeatDays
      repeatStopsOn: $repeatStopsOn
      timeZone: $timeZone
      placeId: $placeId
      primaryCommunityId: $primaryCommunityId
    ) {
      startAtLocal
      endAtLocal
    }
  }
`

type ServerOccurrence = { startAtLocal: string; endAtLocal: string | null }

const DEBOUNCE_DELAY = 400

export const occurrencesPreviewKey = (input: OccurrencesPreviewInput): string => {
  const wallClock = formRawWallClock(input)
  const repeatStopsOnKey = input.repeatStopsOn ? dayjs(input.repeatStopsOn).format('YYYY-MM-DD') : ''
  const repeatDays = input.repeats === RepeatingPatterns.WEEKLY ? selectedRepeatDays(input.repeatDays) : []

  return [
    wallClock.startAtLocal ?? '',
    wallClock.endAtLocal ?? '',
    input.allDay ? '1' : '0',
    input.eventType ?? '',
    input.repeats ?? '',
    repeatDays.join(','),
    repeatStopsOnKey,
    input.timeZone ?? '',
    input.placeId ?? '',
    input.primaryCommunityId ?? '',
  ].join('|')
}

// A SINGLE_EVENT / no-recurrence form stores no series — the server returns
// []; mirror that without even querying (matches EventSeriesCreator: nothing
// persisted there, so the preview shows nothing).
const isExpandable = (input: OccurrencesPreviewInput): boolean =>
  Boolean(
    input.startAtDate &&
      input.eventType &&
      input.eventType !== EventTypes.SINGLE_EVENT &&
      input.repeats &&
      input.repeatStopsOn,
  )

// Build the exact { startAtDate, startAtTime, endAtTime, ... } shape
// SummaryOccurrences renders, from the server's venue wall-clock strings.
// parseLocalWallClock => a LOCAL Date whose wall-clock IS the venue
// wall-clock, so formatTime/formatDayAndDate display what will be stored.
const toModelOccurrences = (occurrences: ServerOccurrence[]): EventStartEndDateTime[] =>
  occurrences.flatMap((o) => {
    const start = parseLocalWallClock(o.startAtLocal)
    if (!start) return []
    const end = parseLocalWallClock(o.endAtLocal) ?? start
    return [new EventStartEndDateTime({ startAt: start, endAt: end })]
  })

type Return = { occurrences: EventStartEndDateTime[] }

const useServerOccurrencesPreview = (input: OccurrencesPreviewInput): Return => {
  const [occurrences, setOccurrences] = useState<EventStartEndDateTime[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)
  const client = useApolloClient()

  // Stable change key over only the recurrence-relevant fields, so unrelated
  // form edits don't re-query and a real change debounce-refreshes.
  const wallClock = formRawWallClock(input)
  const repeatStopsOnKey = input.repeatStopsOn ? dayjs(input.repeatStopsOn).format('YYYY-MM-DD') : ''
  // Only meaningful for WEEKLY; other patterns send [] (start-weekday expansion).
  const repeatDays = input.repeats === RepeatingPatterns.WEEKLY ? selectedRepeatDays(input.repeatDays) : []
  const key = occurrencesPreviewKey(input)

  useEffect(() => () => {
    requestSeqRef.current++
  }, [])

  useEffect(() => {
    if (!isExpandable(input) || !wallClock.startAtLocal) {
      requestSeqRef.current++
      // Stable preview keys break the onChangeModel → setOccurrencesInput → change('occurrences') loop.
      setOccurrences(prev => (prev.length ? [] : prev))
      return
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      const seq = ++requestSeqRef.current
      client
        .query<{ previewEventOccurrences?: ServerOccurrence[] }>({
          query: PREVIEW_EVENT_OCCURRENCES_QUERY,
          fetchPolicy: 'no-cache',
          variables: {
            startAtLocal: wallClock.startAtLocal,
            endAtLocal: wallClock.endAtLocal ?? null,
            allDay: input.allDay ?? false,
            eventType: input.eventType,
            repeats: input.repeats ?? null,
            repeatDays,
            repeatStopsOn: repeatStopsOnKey || null,
            timeZone: input.timeZone || null,
            placeId: input.placeId || null,
            primaryCommunityId: input.primaryCommunityId || null,
          },
        })
        .then(({ data }) => {
          if (seq === requestSeqRef.current) {
            setOccurrences(toModelOccurrences(data?.previewEventOccurrences ?? []))
          }
        })
        .catch(error => {
          console.error('occurrences preview lookup failed:', error.message)
        })
    }, DEBOUNCE_DELAY)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // `key` captures every input field the query depends on (incl. the
    // derived raw wall-clock); re-running on its change is the intended
    // debounce trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, key])

  return { occurrences }
}

export default useServerOccurrencesPreview
